import React, { useEffect, useImperativeHandle, useRef } from 'react';
import styles from './Modal.module.scss';
import { CloseIcon } from '@eventbrite/marmalade';
import type { ModalProps } from './types';

const Modal: React.FC<ModalProps> = React.forwardRef(({ children, onClose }, ref) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target === dialogRef.current) {
        handleOnClose();
      }
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener('click', handleBackdropClick);
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener('click', handleBackdropClick);
      }
    };
  }, [onClose, dialogRef.current]);

  useImperativeHandle(ref, () => ({
    show: () => {
      if (!dialogRef.current) return;
      document.body.classList.add(styles.modalOpen);
      dialogRef.current.showModal();
    },
    close: () => {
      if (!dialogRef.current) return;
      dialogRef.current.close();
    },
  }));

  const handleOnClose = () => {
    if (!dialogRef.current) return;

    if (onClose) {
      document.body.classList.remove(styles.modalOpen);
      onClose();
    }

    dialogRef.current?.close();
  };

  const childrenN = React.Children.count(children);

  const classExtra = childrenN === 1 ? styles.oneChild : '';

  return (
    <dialog className={[styles.dialog, classExtra].join(' ')} ref={dialogRef}>
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.closeButton} onClick={handleOnClose} aria-label="close">
            <CloseIcon size="medium" />
          </button>
        </div>
        <div className={styles.content}>{children}</div>
      </div>
    </dialog>
  );
});

export default Modal;
